import { useEffect } from "react";

const ORIGIN = "https://vidyapeeth360.com";
// WP-01: public/index.html is a NEUTRAL shell (true globals only — no description, OG copy,
// canonical or JSON-LD). Every route owns its metadata via this component, so the rollback
// baseline for a hydrated-prerender document is "remove the tag", not "restore homepage copy".
const DEFAULT_HEAD = Object.freeze({
  title: "Vidyapeeth360",
});

/**
 * Apply route-owned attributes to a head element and return an exact rollback.
 *
 * Marketing pages share one document in the SPA. Restoring the values that were
 * present before this route mounted prevents a module/mobile page from leaking
 * its canonical, social copy or robots directive into the next route.
 */
function applyHeadElement(selector, tagName, attributes, restoreAttributes) {
  let node = document.head.querySelector(selector);
  const created = !node;
  if (!node) {
    node = document.createElement(tagName);
    document.head.appendChild(node);
  }
  const removeOnRollback = created || restoreAttributes === null;
  const restoreSource = restoreAttributes === undefined ? null : restoreAttributes;
  const previous = new Map(Object.keys(attributes).map((key) => {
    if (restoreSource && Object.prototype.hasOwnProperty.call(restoreSource, key)) {
      return [key, restoreSource[key]];
    }
    return [key, node.hasAttribute(key) ? node.getAttribute(key) : null];
  }));
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);

  return () => {
    if (removeOnRollback) {
      node.remove();
      return;
    }
    for (const [key, value] of previous.entries()) {
      if (value === null) node.removeAttribute(key);
      else node.setAttribute(key, value);
    }
  };
}

/**
 * Route-scoped metadata for SPA navigation and SEO prerender snapshots.
 *
 * `image` (optional, SEO-10): a site-relative path (e.g. "/og/og-pricing.jpg") to a truthful
 * route-specific social card. When set it overrides the shell's global og:image/twitter:image;
 * rollback restores the global card. Never point it at an asset carrying unverified claims.
 */
export default function MarketingSeo({ title, description, path, type = "website", schema, robots, image }) {
  const schemaJson = JSON.stringify(schema);
  useEffect(() => {
    const canonicalUrl = `${ORIGIN}${path}`;
    // On a direct request, the prerendered document already contains this route's
    // metadata before React hydrates. Treat it as route-owned, not as a baseline
    // worth restoring when the user later navigates elsewhere in the SPA.
    const hydratedPrerender = document.head.querySelector('link[rel="canonical"]')?.href === canonicalUrl;
    const previousTitle = hydratedPrerender ? DEFAULT_HEAD.title : document.title;
    // Hydrated prerender: the document's head tags were baked for THIS route. On rollback the
    // neutral shell had no description/OG copy/canonical, so those tags are removed (null),
    // while og:type rolls back to the shell's global default.
    const restore = hydratedPrerender ? {
      description: null,
      ogTitle: null,
      ogDescription: null,
      ogUrl: null,
      ogType: { property: "og:type", content: "website" },
      twitterTitle: null,
      twitterDescription: null,
      canonical: null,
      robots: null,
      googlebot: null,
    } : null;
    const rollbacks = [];

    document.title = title;
    rollbacks.push(
      applyHeadElement('meta[name="description"]', "meta", { name: "description", content: description }, restore?.description),
      applyHeadElement('meta[property="og:title"]', "meta", { property: "og:title", content: title }, restore?.ogTitle),
      applyHeadElement('meta[property="og:description"]', "meta", { property: "og:description", content: description }, restore?.ogDescription),
      applyHeadElement('meta[property="og:url"]', "meta", { property: "og:url", content: canonicalUrl }, restore?.ogUrl),
      applyHeadElement('meta[property="og:type"]', "meta", { property: "og:type", content: type }, restore?.ogType),
      applyHeadElement('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: title }, restore?.twitterTitle),
      applyHeadElement('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: description }, restore?.twitterDescription),
      applyHeadElement('link[rel="canonical"]', "link", { rel: "canonical", href: canonicalUrl }, restore?.canonical)
    );
    if (robots) {
      rollbacks.push(
        applyHeadElement('meta[name="robots"]', "meta", { name: "robots", content: robots }, restore?.robots),
        applyHeadElement('meta[name="googlebot"]', "meta", { name: "googlebot", content: robots }, restore?.googlebot),
      );
    }
    if (image) {
      const imageUrl = `${ORIGIN}${image}`;
      // The neutral shell ships ONE global card; a route override rolls back to it on unmount
      // (both on plain SPA navigation and after hydrating this route's prerendered document).
      const globalCard = { content: `${ORIGIN}/og-image.jpg` };
      rollbacks.push(
        applyHeadElement('meta[property="og:image"]', "meta", { property: "og:image", content: imageUrl },
          hydratedPrerender ? { property: "og:image", ...globalCard } : undefined),
        applyHeadElement('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: imageUrl },
          hydratedPrerender ? { name: "twitter:image", ...globalCard } : undefined),
      );
    }

    // A route owns at most one structured-data block. Delete abandoned blocks
    // from a previous render before publishing this route's schema.
    document.head.querySelectorAll('script[data-vp-route-seo="true"]').forEach((node) => node.remove());
    let script = null;
    if (schemaJson) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.vpRouteSeo = "true";
      script.textContent = schemaJson;
      document.head.appendChild(script);
    }

    return () => {
      script?.remove();
      for (const rollback of rollbacks.reverse()) rollback();
      document.title = previousTitle;
    };
  }, [description, image, path, robots, schemaJson, title, type]);

  return null;
}

/**
 * Keep internal visual QA galleries usable while explicitly excluding them
 * from search indexes. The prerenderer also emits these directives into the
 * deployed HTML, so this is effective before and after React boots.
 */
export function InternalNoIndex({ children, path, title }) {
  useEffect(() => {
    const canonicalUrl = `${ORIGIN}${path}`;
    const hydratedPrerender = document.head.querySelector('link[rel="canonical"]')?.href === canonicalUrl;
    const previousTitle = hydratedPrerender ? DEFAULT_HEAD.title : document.title;
    const rollbacks = [
      applyHeadElement('meta[name="robots"]', "meta", {
        name: "robots",
        content: "noindex, nofollow, noarchive",
      }, hydratedPrerender ? null : undefined),
      applyHeadElement('meta[name="googlebot"]', "meta", {
        name: "googlebot",
        content: "noindex, nofollow, noarchive",
      }, hydratedPrerender ? null : undefined),
      applyHeadElement(
        'link[rel="canonical"]',
        "link",
        { rel: "canonical", href: canonicalUrl },
        // Neutral shell has no canonical — a hydrated prerender rolls back to removal.
        hydratedPrerender ? null : undefined
      ),
    ];
    document.title = title;

    return () => {
      for (const rollback of rollbacks.reverse()) rollback();
      document.title = previousTitle;
    };
  }, [path, title]);

  return children;
}

export const seoOrigin = ORIGIN;
